import React, { useEffect, useRef } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import 'leaflet.heat'

export interface HeatmapPoint {
  city: string
  lat: number
  lng: number
  weight: number
  units: number
}

interface Props {
  points: HeatmapPoint[]
  height?: number
  /** Optional click-to-filter handler invoked with the city name when a marker is clicked. */
  onCityClick?: (city: string) => void
  /** Cities currently active in the filter — rendered with a highlighted marker. */
  selectedCities?: string[]
}

// Portugal-focused default view (lat/lng roughly centered).
const DEFAULT_CENTER: [number, number] = [39.6, -8.0]
const DEFAULT_ZOOM = 6

/**
 * City-aggregated heatmap using Leaflet + leaflet.heat + free Carto Light tiles.
 * Intentionally bypasses react-leaflet for the heat layer because the plugin
 * attaches imperatively via `L.heatLayer(...)`.
 */
export function HeatmapLeafletMap({
  points,
  height = 420,
  onCityClick,
  selectedCities,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<L.Map | null>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const heatLayerRef = useRef<any>(null)
  const markerLayerRef = useRef<L.LayerGroup | null>(null)
  // Remember the last-computed bounds so a container resize (e.g. the chart
  // being rendered inside an expanded modal) can re-apply the fit instead of
  // leaving the view stuck where it was when the points first loaded.
  const lastBoundsRef = useRef<L.LatLngBounds | null>(null)
  // Keep the latest handler / selection in refs so the marker effect can read
  // them without forcing a map tear-down/rebuild on every click.
  const onCityClickRef = useRef<Props['onCityClick']>(onCityClick)
  const selectedCitiesRef = useRef<string[]>(selectedCities ?? [])
  useEffect(() => {
    onCityClickRef.current = onCityClick
  }, [onCityClick])
  useEffect(() => {
    selectedCitiesRef.current = selectedCities ?? []
  }, [selectedCities])

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return

    const container = containerRef.current
    const map = L.map(container, {
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      scrollWheelZoom: true,
    })

    L.tileLayer(
      'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
      {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
        subdomains: 'abcd',
        maxZoom: 19,
      },
    ).addTo(map)

    mapRef.current = map

    // Leaflet measures the container once on init and does not observe
    // resizes. When the map is rendered inside a modal / expanding panel /
    // flex layout, the container's final width arrives after mount and the
    // tiles load for a narrow strip only. Invalidate on any container size
    // change to keep the tile grid aligned with the real viewport, and
    // re-apply the last fitBounds so the view re-centers in the new box
    // (otherwise zoom/center stay computed off the stale initial size).
    const invalidate = () => {
      map.invalidateSize()
      const bounds = lastBoundsRef.current
      if (bounds && bounds.isValid()) {
        map.fitBounds(bounds, { padding: [30, 30], maxZoom: 9 })
      }
    }
    const observer = new ResizeObserver(invalidate)
    observer.observe(container)
    // Also invalidate on the next frame so the initial paint catches up
    // with the real (post-layout) width.
    const raf = window.requestAnimationFrame(invalidate)

    return () => {
      observer.disconnect()
      window.cancelAnimationFrame(raf)
      map.remove()
      mapRef.current = null
      heatLayerRef.current = null
    }
  }, [])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    if (heatLayerRef.current) {
      map.removeLayer(heatLayerRef.current)
      heatLayerRef.current = null
    }

    if (points.length === 0) return

    const maxWeight = Math.max(...points.map((p) => p.weight)) || 1
    // Normalise weights into [0.1, 1] so the heat layer has meaningful gradient stops
    const heatPoints = points.map(
      (p) => [p.lat, p.lng, Math.max(0.1, p.weight / maxWeight)] as [number, number, number],
    )

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const layer = (L as any).heatLayer(heatPoints, {
      radius: 28,
      blur: 22,
      maxZoom: 10,
      gradient: {
        0.2: '#2563eb',
        0.4: '#16a34a',
        0.6: '#f59e0b',
        0.8: '#ef4444',
        1.0: '#7f1d1d',
      },
    })

    layer.addTo(map)
    heatLayerRef.current = layer

    // Ensure the tile grid & projection match the real container size before
    // computing bounds — otherwise a modal-mounted map computes fit against
    // a pre-layout narrow viewport and the tiles render offset to one side.
    map.invalidateSize()

    // Fit bounds to data when we have multiple points
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng] as [number, number]))
    if (bounds.isValid()) {
      lastBoundsRef.current = bounds
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 9 })
    }
  }, [points])

  // Render invisible-ish circle markers per city so the heatmap can still act as a
  // click-to-filter surface. Kept in a dedicated layer group so it can be wiped
  // and redrawn cheaply when the data or the selection changes.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    if (markerLayerRef.current) {
      map.removeLayer(markerLayerRef.current)
      markerLayerRef.current = null
    }

    if (points.length === 0) return

    const group = L.layerGroup()
    for (const p of points) {
      const selected = selectedCitiesRef.current.includes(p.city)
      const marker = L.circleMarker([p.lat, p.lng], {
        radius: selected ? 10 : 7,
        color: selected ? '#0f172a' : '#1d4ed8',
        weight: selected ? 2.5 : 1.5,
        fillColor: selected ? '#1d4ed8' : '#ffffff',
        fillOpacity: selected ? 0.85 : 0.25,
        interactive: true,
      })
      marker.bindTooltip(
        `<strong>${p.city}</strong><br/>${p.units.toLocaleString()} units · €${Math.round(
          p.weight,
        ).toLocaleString()}/m²`,
        { direction: 'top', offset: [0, -6] },
      )
      marker.on('click', () => {
        onCityClickRef.current?.(p.city)
      })
      group.addLayer(marker)
    }
    group.addTo(map)
    markerLayerRef.current = group
  }, [points, selectedCities])

  return (
    <div
      ref={containerRef}
      style={{ height, width: '100%' }}
      className="rounded-lg overflow-hidden border border-secondary-200"
    />
  )
}
